/**
 * Color / palette transform.
 *
 * Applies the selected palette and any per-series color overrides.
 */
import type { EChartsOption } from 'echarts';
import type { ChartStyleState } from '../../../../edit/visualConfig';
import { getPaletteColors } from '../../../../edit/visualConfig';

export function applyColors(
  option: EChartsOption,
  vc: ChartStyleState,
  isDark: boolean,
): EChartsOption {
  const result = { ...option };
  result.color = getPaletteColors(vc.palette, isDark);

  // Per-series color overrides (e.g. user picks a custom color for "Europe")
  const overrides = vc.seriesColors ?? {};
  if (Object.keys(overrides).length > 0 && Array.isArray(result.series)) {
    const isCategoryMode = vc.typeStyle?.colorMode === 'category';

    if (isCategoryMode) {
      // Per-category color overrides: map category names → colors on each data point
      const xAxis = (result as any).xAxis;
      const categories: string[] =
        xAxis && !Array.isArray(xAxis) ? (xAxis.data ?? []) : [];

      if (categories.length > 0) {
        result.series = (result.series as any[]).map((s: any) => {
          if (!Array.isArray(s.data)) return s;
          return {
            ...s,
            data: s.data.map((val: any, i: number) => {
              const catName = String(categories[i] ?? '');
              const color = overrides[catName];
              if (!color) return val;
              const item =
                typeof val === 'object' && val !== null ? { ...val } : { value: val };
              item.itemStyle = { ...item.itemStyle, color };
              return item;
            }),
          };
        });
      }
    } else {
      // Per-series + per-data-item color overrides (uniform mode)
      result.series = (result.series as any[]).map((s: any) => {
        // Per-series override (cartesian charts: bar, line, area, scatter)
        const seriesColor = overrides[s.name];
        const patched = seriesColor
          ? { ...s, itemStyle: { ...s.itemStyle, color: seriesColor }, lineStyle: { ...s.lineStyle, color: seriesColor } }
          : { ...s };

        // Per-data-item override (named data items: pie, funnel, etc.)
        if (Array.isArray(patched.data)) {
          let hasNamedItem = false;
          const patchedData = patched.data.map((item: any) => {
            if (typeof item === 'object' && item !== null && 'name' in item) {
              hasNamedItem = true;
              const color = overrides[item.name];
              if (color) return { ...item, itemStyle: { ...item.itemStyle, color } };
            }
            return item;
          });
          if (hasNamedItem) patched.data = patchedData;
        }

        return patched;
      });
    }
  }

  return result;
}